
import React, { useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { SystemSettings } from "@/components/admin/SystemSettings";
import { MetaAccountsManager } from "@/components/admin/MetaAccountsManager";
import { SecuritySettings } from "@/components/admin/SecuritySettings";
import { UserManagement } from "@/components/admin/UserManagement";
import { TutorialManager } from "@/components/admin/TutorialManager";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { LayoutDashboard, Users, FileText, ShieldCheck, RefreshCw } from "lucide-react";
import { useUserStats } from "@/hooks/useUserStats";
import { Button } from "@/components/ui/button";

// Create a simple AdminDashboardHome component for the main dashboard view
const AdminDashboardHome = () => {
  const { activeUsers, totalUsers, metaAccounts, loading } = useUserStats();
  
  return (
    <div className="space-y-6">
      <Card className="backdrop-blur-sm bg-white/10 border border-gray-700/30">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-6 w-6 text-[#41c7f0]" />
            <CardTitle className="text-xl font-semibold text-white">Admin Dashboard</CardTitle>
          </div>
          <CardDescription className="text-gray-400 mt-0">
            Echtzeit-Übersicht aller Systemdaten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white/5 border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#41c7f0]" />
                  System Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  {loading ? "Wird überprüft..." : "Alle Systeme aktiv"}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#41c7f0]" />
                  Aktive Benutzer
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin text-gray-400" />
                    <p className="text-gray-400">Wird geladen...</p>
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-[#41c7f0]">{activeUsers}</p>
                    <p className="text-sm text-gray-400">von insgesamt {totalUsers} Benutzern</p>
                  </>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#41c7f0]" />
                  Meta Accounts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin text-gray-400" />
                    <p className="text-gray-400">Wird geladen...</p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-[#41c7f0]">{metaAccounts}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the tab from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const activeTab = urlParams.get("tab") || "dashboard";
  
  // Extra safety check to ensure only admin users can access this page
  useEffect(() => {
    if (!isAdmin) {
      console.log("Non-admin user accessed admin dashboard, redirecting to home");
      navigate("/", { replace: true });
    }
  }, [isAdmin, navigate]);

  // Render the appropriate component based on the active tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case "dashboard":
        return <AdminDashboardHome />;
      case "users":
        return <UserManagement />;
      case "meta":
        return <MetaAccountsManager />;
      case "tutorials":
        return <TutorialManager />;
      case "system":
        return <SystemSettings />;
      case "security":
        return <SecuritySettings />;
      default:
        return <AdminDashboardHome />;
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-4">
        {renderActiveComponent()}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
